import { useState, useCallback } from 'react';
import { useAppDispatch } from '../../../../../../app/store';
import { addCallLog } from '../../../../../../services/CaseManagementService/caseManageSlice';
import { useGetMyProfileQuery } from '../../../../../../services/ProfileService';

export interface CallLogFormData {
  callTo: string;
  isCallAnswered: boolean;
  notes: string;
  callDate: string;
}

export interface CallLogFormErrors {
  callTo: string;
  notes: string;
}

interface UseCallLogFormReturn {
  formData: CallLogFormData;
  errors: CallLogFormErrors;
  isValid: boolean;
  updateField: (field: keyof CallLogFormData, value: string | boolean) => void;
  validateForm: () => boolean;
  handleSubmit: () => void;
  handleReset: () => void;
}

const getInitialFormData = (): CallLogFormData => ({
  callTo: '',
  isCallAnswered: false,
  notes: '',
  callDate: new Date().toISOString().slice(0, 16)
});

const initialErrors: CallLogFormErrors = {
  callTo: '',
  notes: ''
};

export const useCallLogForm = (onSuccess?: () => void): UseCallLogFormReturn => {
  const [formData, setFormData] = useState<CallLogFormData>(getInitialFormData);
  const [errors, setErrors] = useState<CallLogFormErrors>(initialErrors);
  
  const dispatch = useAppDispatch();
  const { data: profile } = useGetMyProfileQuery();

  const validateForm = useCallback((): boolean => {
    const newErrors: CallLogFormErrors = {
      callTo: '',
      notes: ''
    };

    if (!formData.callTo.trim()) {
      newErrors.callTo = 'Call recipient is required';
    }

    if (!formData.notes.trim()) {
      newErrors.notes = 'Call notes are required';
    }

    setErrors(newErrors);
    return !newErrors.callTo && !newErrors.notes;
  }, [formData.callTo, formData.notes]);

  const isValid = formData.callTo.trim().length > 0 && formData.notes.trim().length > 0;

  const updateField = useCallback((field: keyof CallLogFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (field in errors && errors[field as keyof CallLogFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleSubmit = useCallback(() => {
    if (!validateForm()) return;

    try {
      const callLogData = {
        callTo: formData.callTo.trim(),
        isCallAnswered: formData.isCallAnswered,
        notes: formData.notes.trim(),
        callDate: new Date(formData.callDate).toISOString(),
        callBy: profile?.name || 'Unknown User'
      };

      dispatch(addCallLog(callLogData));
      onSuccess?.();
    } catch (error) {
      console.error('Failed to add call log:', error);
    }
  }, [formData, profile, dispatch, validateForm, onSuccess]);

  const handleReset = useCallback(() => {
    setFormData(getInitialFormData());
    setErrors(initialErrors);
  }, []);

  return {
    formData,
    errors,
    isValid,
    updateField,
    validateForm,
    handleSubmit,
    handleReset
  };
};
